/* eslint-disable no-underscore-dangle */
import logger from 'UTILS/logger';
import Notifier from '../Notifier';
import {
  clearCart,
  fetchCart,
  addToCart as add,
  changeCartLineItem,
} from '../fetch/shopify-apis/cart';

let cart; // used for Singleton - there can only be 1

/**
 * This manages the Cart, updating state from various cart actions.
 * This is an Observer than can be subscribed to.
 *
 * Subscription types include: 'beforeLoad', 'afterLoad', 'beforeUpdate', 'afterUpdate', 'error.
 *
 * Each notification returns cart data or error & the name of caller
 *
 * TODO: Update cart state after addToCart - we don't get back an updated cart in the response, so need to handle this ourselves - eg. update subtotal
 *
 * @returns {Array} obj.items All the cart items.
 * @returns {Number} obj.count The total count.
 * @returns {Number} obj.totalPrice Total cost of everything in the cart.
 * */
export default class CartObserver extends Notifier {
  constructor() {
    super();
    if (cart === undefined) {
      this.items = [];
      // Bind for safety as these may be passed around and lose context
      this.addToCart = this.addToCart.bind(this);
      this.removeFromCartById = this.removeFromCartById.bind(this);
      this.getCart = this.getCart.bind(this);
      this.changeQuantityById = this.changeQuantityById.bind(this);
      cart = this;
    } else {
      // return the object instead to enforce a Singleton
      /* eslint-disable no-constructor-return */
      return cart;
    }
  }

  _addCartRes(cartData) {
    Object.assign(this, cartData);
  }

  /**
   * Add the returned response to state
   * @param {object} newItem The response json from the request. This response is different from other cart actions, which return the updated cart.
   */
  _addItem(newItem) {
    const existingIndex = this.items.findIndex((itm) => {
      return itm.id === newItem.id;
    });
    if (existingIndex !== -1) {
      this.items[existingIndex] = newItem;
    } else {
      this.items = [...this.items, newItem];
    }
    this._updateCount();
  }

  _updateCount() {
    this.item_count = this.items.reduce((prev, curr) => {
      return prev + curr.quantity;
    }, 0);
  }

  /**
   * Gets the cart and inits it in state
   */
  async getCart() {
    try {
      this._handleNotify('beforeLoad', 'getCart');
      const data = await fetchCart();
      this._addCartRes(data); // give it the res data which accurately increments the quantity
      this._handleNotify('afterLoad', 'getCart');
    } catch (e) {
      this._handleNotify('error', 'getCart', e);
    }
  }

  /**
   * Add a single item to the cart. Usually from the product form or direct from from a collection
   * @param {FormData | JSON} item Receive FormData or json object. Needs to contain keys of variant id and quantity.
   */
  async addToCart(item) {
    try {
      this._handleNotify('beforeUpdate', 'addToCart');
      const data = await add(item);
      this._addItem(data);
      this._handleNotify('afterUpdate', 'addToCart');
    } catch (e) {
      logger.error('addToCart', e);
      this._handleNotify('error', 'addToCart', e);
    }
  }

  /**
   * Handles removing a line item from the cart.
   *
   * The Cart Api response returns the whole cart, so this is used to update state.
   * @param {string} id Preferably the line_item id, but variant id will work as well
   */
  async removeFromCartById(id) {
    try {
      this._handleNotify('beforeUpdate', 'removeFromCartById');
      const data = await changeCartLineItem({ id, quantity: 0 });
      this._addCartRes(data);
      this._handleNotify('afterUpdate', 'removeFromCartById');
    } catch (e) {
      logger.error('removeFromCartById', e);
      this._handleNotify('error', 'removeFromCartById', e);
    }
  }

  /**
   * Handles updating the quantity from a line item in the cart.
   *
   * The Cart Api response returns the whole cart, so this is used to update state.
   * @param {string} id Preferably the line_item id, but variant id will work as well
   * @param {number | string} quantity The updated quantity
   */
  async changeQuantityById(id, quantity) {
    try {
      this._handleNotify('beforeUpdate', 'changeQuantityById');
      const data = await changeCartLineItem({ id, quantity });
      this._addCartRes(data);
      this._handleNotify('afterUpdate', 'changeQuantityById');
    } catch (e) {
      logger.error('changeQuantityById', e);
      this._handleNotify('error', 'changeQuantityById', e);
    }
  }

  async clearAll() {
    try {
      this._handleNotify('beforeUpdate', 'clearAll');
      await clearCart();
      this._handleNotify('afterUpdate', 'clearAll');
    } catch (e) {
      logger.error('clearAll', e);
      this._handleNotify('error', 'clearAll', e);
    }
  }

  _handleNotify(type, caller, error) {
    if (error) {
      return this.notify(type, error, caller);
    }
    return this.notify(
      type,
      {
        items: this.items,
        count: this.item_count,
        totalPrice: this.total_price,
      },
      caller
    );
  }
}
